import type { NetMessage } from './transport';
import type { PlacedLocation, TileDelta } from '../types/map.types';

/** Host → clients: launch a dungeon now (party freezes for the run). */
export interface EnterDungeonMsg extends NetMessage {
  type: 'enter-dungeon';
  dungeonId: string;
}
/** Host → clients: the run ended, come back to the shared hub. */
export interface ReturnToHubMsg extends NetMessage {
  type: 'return-to-hub';
}
/** Host → clients: the host closed the session. */
export interface SessionClosedMsg extends NetMessage {
  type: 'session-closed';
}
/** Client → host: my dungeon view is mounted, (re)send the floor. */
export interface RequestFloorMsg extends NetMessage {
  type: 'request-floor';
}

export function makeEnterDungeon(dungeonId: string): EnterDungeonMsg {
  return { type: 'enter-dungeon', dungeonId };
}
export const RETURN_TO_HUB: ReturnToHubMsg = { type: 'return-to-hub' };
export const SESSION_CLOSED: SessionClosedMsg = { type: 'session-closed' };
export const REQUEST_FLOOR: RequestFloorMsg = { type: 'request-floor' };

/** Host → clients: a floor transition started. The client raises its own cover
 *  and blocks input for the full transition (it self-ends after the standard
 *  duration), so it can't act while the host's shared sim is frozen. */
export interface FloorTransitionStartMsg extends NetMessage {
  type: 'floor-transition-start';
  floor: number;
}
export function makeFloorTransitionStart(
  floor: number,
): FloorTransitionStartMsg {
  return { type: 'floor-transition-start', floor };
}

export type LocationDelta =
  | { op: 'add'; location: PlacedLocation }
  | { op: 'remove'; id: string }
  | { op: 'update'; id: string; used?: boolean; destroyed?: boolean };

export interface WorldUpdateMsg extends NetMessage {
  type: 'world-update';
  locations?: LocationDelta[];
  /** Runtime tile-type changes (challenge-room seal/restore). */
  tiles?: TileDelta[];
}
/** Host → clients: this player died; the owning peer resolves it (it has the
 *  client-local inventory, so only it knows whether it carries a revive seed). */
export interface PlayerDiedMsg extends NetMessage {
  type: 'player-died';
  playerId: string;
  cell: { x: number; y: number };
}
/** Owning client → host: the outcome of its own death. `revived` = it spent a
 *  seed (respawn in place); otherwise it goes down (beacon + spectator). */
export interface DeathResolutionMsg extends NetMessage {
  type: 'death-resolution';
  playerId: string;
  cell: { x: number; y: number };
  revived: boolean;
}
export interface PlayerRevivedMsg extends NetMessage {
  type: 'player-revived';
  playerId: string;
  cell: { x: number; y: number };
}
export interface ReviveRequestMsg extends NetMessage {
  type: 'revive-request';
  deadPlayerId: string;
}
/** Why the run ended for everyone: a total party kill, or the host deliberately
 *  abandoning. Clients show a different notice for each. */
export type RunWipeReason = 'tpk' | 'abandon';
export interface RunWipeMsg extends NetMessage {
  type: 'run-wipe';
  reason: RunWipeReason;
}

export function makeWorldUpdate(locations: LocationDelta[]): WorldUpdateMsg {
  return { type: 'world-update', locations };
}
export function makeTileMutation(tiles: TileDelta[]): WorldUpdateMsg {
  return { type: 'world-update', tiles };
}
export function makePlayerDied(
  playerId: string,
  cell: { x: number; y: number },
): PlayerDiedMsg {
  return { type: 'player-died', playerId, cell };
}
export function makeDeathResolution(
  playerId: string,
  cell: { x: number; y: number },
  revived: boolean,
): DeathResolutionMsg {
  return { type: 'death-resolution', playerId, cell, revived };
}
export function makePlayerRevived(
  playerId: string,
  cell: {
    x: number;
    y: number;
  },
): PlayerRevivedMsg {
  return { type: 'player-revived', playerId, cell };
}
export function makeReviveRequest(deadPlayerId: string): ReviveRequestMsg {
  return { type: 'revive-request', deadPlayerId };
}
export function makeRunWipe(reason: RunWipeReason): RunWipeMsg {
  return { type: 'run-wipe', reason };
}

export interface MonsterKilledMsg extends NetMessage {
  type: 'monster-killed';
  configId: string;
  /** Monster instance level — drives each browser's per-player XP formula. */
  level: number;
  positions: { x: number; y: number }[];
  isElite: boolean;
  isMiniBoss: boolean;
  isBoss: boolean;
  /** Entity id that landed the killing blow (or undefined). Only this player's
   *  private roll gets the rich-drop multiplier. */
  killerId?: string;
  richDropMultiplier: number;
}

export function makeMonsterKilled(
  configId: string,
  level: number,
  positions: { x: number; y: number }[],
  isElite: boolean,
  isMiniBoss: boolean,
  isBoss: boolean,
  killerId: string | undefined,
  richDropMultiplier: number,
): MonsterKilledMsg {
  return {
    type: 'monster-killed',
    configId,
    level,
    positions,
    isElite,
    isMiniBoss,
    isBoss,
    killerId,
    richDropMultiplier,
  };
}

export interface LocationFireMsg extends NetMessage {
  type: 'location-fire';
  locationId: string;
  x: number;
  y: number;
  /** Once-per-player remote: only the client whose localPlayerId matches acts.
   *  Absent for once-per-spawn (every client acts). */
  targetPlayerId?: string;
}

export function makeLocationFire(
  locationId: string,
  x: number,
  y: number,
  targetPlayerId?: string,
): LocationFireMsg {
  return { type: 'location-fire', locationId, x, y, targetPlayerId };
}

/** Client → host: I deliberately leave the run (forfeit). The host removes my
 *  avatar for good (not held for reconnect) and re-checks TPK. */
export interface LeaveRunMsg extends NetMessage {
  type: 'leave-run';
}
export const LEAVE_RUN: LeaveRunMsg = { type: 'leave-run' };

export interface ThrowItemMsg extends NetMessage {
  type: 'throw-item';
  itemId: string;
  quantity: number;
  x: number;
  y: number;
}
export function makeThrowItem(
  itemId: string,
  quantity: number,
  x: number,
  y: number,
): ThrowItemMsg {
  return { type: 'throw-item', itemId, quantity, x, y };
}

export interface CommonItemAddMsg extends NetMessage {
  type: 'common-item-add';
  id: string;
  itemId: string;
  quantity: number;
  x: number;
  y: number;
  dropperId: string;
}
export function makeCommonItemAdd(
  id: string,
  itemId: string,
  quantity: number,
  x: number,
  y: number,
  dropperId: string,
): CommonItemAddMsg {
  return { type: 'common-item-add', id, itemId, quantity, x, y, dropperId };
}

export interface CommonPickupRequestMsg extends NetMessage {
  type: 'common-pickup-request';
  id: string;
}
export function makeCommonPickupRequest(id: string): CommonPickupRequestMsg {
  return { type: 'common-pickup-request', id };
}

export interface CommonItemRemoveMsg extends NetMessage {
  type: 'common-item-remove';
  id: string;
  byPlayerId: string;
}
export function makeCommonItemRemove(
  id: string,
  byPlayerId: string,
): CommonItemRemoveMsg {
  return { type: 'common-item-remove', id, byPlayerId };
}
